import React, { useMemo } from 'react';

import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import StyledTable from 'components/Companies/table/StyledTable';
import { isMysteryShopAdmin } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { MAILBOX_CONNECTION_STATUS, MAILBOX_CONNECTION_STATUS_LABELS } from '../constants';
import { getEffectiveMailboxConnectionStatus } from './helpers';
import { useShopperEmailsContext } from './ShopperEmailsContext';
import ShopperEmailsTableActions from './ShopperEmailsTableActions';
import { ShopperEmailType } from '../types';

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 0;
`;

const BaseTable = (props: any) => {
  return <StyledTable {...props} />;
};

const ShopperEmailsStyledTable = styled(BaseTable)`
  &&&&&& {
    .actions-hover-reveal {
      visibility: hidden;
    }

    .ant-table-cell-row-hover {
      .actions-hover-reveal {
        visibility: visible;
      }
    }

    .ant-table-pagination.ant-pagination {
      padding-bottom: 12px;
    }
  }
`;

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PrimaryValue = styled.span`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  word-break: break-word;
`;

const SecondaryValue = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
  word-break: break-word;
`;

const CountValue = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const LabelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid var(--line-gray);
  border-radius: 999px;
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--white);
`;

const StatusBadge = styled.span<{ status?: string | null }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 2px 8px;
  white-space: nowrap;
  border: 1px solid
    ${({ status }) => {
      if (status === MAILBOX_CONNECTION_STATUS.ACTIVE) {
        return 'var(--positive-green)';
      }

      if (status === MAILBOX_CONNECTION_STATUS.NEEDS_RECONNECT) {
        return 'var(--negative-orange)';
      }

      if (status === MAILBOX_CONNECTION_STATUS.REVOKED || status === MAILBOX_CONNECTION_STATUS.ERROR) {
        return 'var(--error-red)';
      }

      return 'var(--line-gray)';
    }};
  border-radius: 999px;
  font-size: 12px;
  color: ${({ status }) => {
    if (status === MAILBOX_CONNECTION_STATUS.ACTIVE) {
      return 'var(--positive-green)';
    }

    if (status === MAILBOX_CONNECTION_STATUS.NEEDS_RECONNECT) {
      return 'var(--negative-orange)';
    }

    if (status === MAILBOX_CONNECTION_STATUS.REVOKED || status === MAILBOX_CONNECTION_STATUS.ERROR) {
      return 'var(--error-red)';
    }

    return 'var(--text-secondary)';
  }};
`;

const renderPaginationTotal = (total: number, range: number[]) => {
  const displayedItems = range[1] - range[0] + 1;
  return (
    <span>
      Showing {displayedItems} of {total} records
    </span>
  );
};

const ShopperEmailsTable = () => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const { loading, items, totalItemsCount, filters, authenticatingEmailId, setPage, openEditModal, onMailboxAction } =
    useShopperEmailsContext();
  const showOwnerColumn = Boolean(
    currentUser && (currentUser.isSuperuser || currentUser.isStaff || isMysteryShopAdmin(currentUser))
  );

  const columns = useMemo<ColumnsType<ShopperEmailType>>(
    () => [
      {
        title: 'Contact',
        dataIndex: 'name',
        key: 'contact',
        render: (_value: string | undefined, record: ShopperEmailType) => {
          const name = record.name?.trim();
          const phone = record.phone?.trim();

          return (
            <ValueContainer>
              {name ? (
                <>
                  <PrimaryValue>{name}</PrimaryValue>
                  {record.email ? <SecondaryValue>{record.email}</SecondaryValue> : null}
                </>
              ) : (
                <PrimaryValue>{record.email || phone || 'Shopper contact'}</PrimaryValue>
              )}
              {phone && (name || record.email) ? <SecondaryValue>{phone}</SecondaryValue> : null}
            </ValueContainer>
          );
        },
      },
      ...(showOwnerColumn
        ? [
            {
              title: 'Owner',
              dataIndex: 'owner',
              key: 'owner',
              width: '18%',
              render: (_value: ShopperEmailType['owner'], record: ShopperEmailType) => {
                const ownerName = record.owner?.full_name?.trim() || record.owner?.email || 'Unassigned';
                const showOwnerEmail = Boolean(record.owner?.email && record.owner.email !== ownerName);

                return (
                  <ValueContainer>
                    <PrimaryValue>{ownerName}</PrimaryValue>
                    {showOwnerEmail ? <SecondaryValue>{record.owner?.email}</SecondaryValue> : null}
                  </ValueContainer>
                );
              },
            },
          ]
        : []),
      {
        title: 'Labels',
        dataIndex: 'labels',
        key: 'labels',
        width: '24%',
        render: (value?: string[]) => {
          if (!value?.length) {
            return null;
          }

          return (
            <LabelsContainer>
              {value.map((label) => (
                <LabelBadge key={label}>{label}</LabelBadge>
              ))}
            </LabelsContainer>
          );
        },
      },
      {
        title: 'Linked Shop Requests',
        dataIndex: 'linked_shop_request_count',
        key: 'linked_shop_request_count',
        width: '16%',
        render: (value?: number | null) => <CountValue>{value ?? 0}</CountValue>,
      },
      {
        title: 'Mailbox Status',
        dataIndex: 'mailbox_connection_status',
        key: 'mailbox_connection_status',
        width: '18%',
        render: (_value: string | null | undefined, record: ShopperEmailType) => {
          const status = getEffectiveMailboxConnectionStatus({
            mailboxConnectionId: record.mailbox_connection_id,
            mailboxConnectionStatus: record.mailbox_connection_status,
          });

          return (
            <StatusBadge status={status}>
              {record.email
                ? (MAILBOX_CONNECTION_STATUS_LABELS[status || 'default'] ?? MAILBOX_CONNECTION_STATUS_LABELS.default)
                : 'No email'}
            </StatusBadge>
          );
        },
      },
      {
        title: '',
        key: 'actions',
        width: 110,
        render: (_value: unknown, record: ShopperEmailType) => (
          <ShopperEmailsTableActions
            authenticatingEmailId={authenticatingEmailId}
            onEdit={openEditModal}
            onLinkMailbox={onMailboxAction}
            shopperEmail={record}
          />
        ),
      },
    ],
    [authenticatingEmailId, onMailboxAction, openEditModal, showOwnerColumn]
  );

  return (
    <Container>
      <ShopperEmailsStyledTable
        columns={columns}
        dataSource={items}
        rowKey={(record: ShopperEmailType) => record.id}
        loading={loading}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: filters.page,
          pageSize: filters.pageSize,
          total: totalItemsCount,
          onChange: (pageNumber: number) => {
            setPage(pageNumber);
          },
          showTotal: renderPaginationTotal,
        }}
      />
    </Container>
  );
};

export default ShopperEmailsTable;
